/**
 * StaffCarte — vue staff de la carte.
 *
 * Source ALIGNÉE sur le client (CarteScreen) : on lit l'arbre de navigation
 * Ciss (`/pos/navigation/admin` si admin, sinon `/pos/navigation` public) au
 * lieu de l'ancienne liste plate Cashpad (`/pos/carte`). Staff et client voient
 * donc exactement la même arborescence.
 *
 * Différence staff : les nœuds/produits `hidden` (masqués côté client) restent
 * AFFICHÉS ici, avec un badge « Masqué » + opacité réduite, pour que le staff
 * sache lesquels ne sont pas visibles du client.
 *
 * Enrichissement : `/pos/navigation/admin` n'attache PAS les overlays enriched
 * (contrairement à `/pos/navigation`). On les récupère donc à part via
 * `/pos/products-enriched` (public) et on les mappe par cashpadId côté client —
 * uniforme quelle que soit la source navigation utilisée. Pas de modif backend.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './staff-design-system.css';
import './Staff.css';
import { Pill } from '../../components/staff/ui/Pill';
import { SectionHeader } from '../../components/staff/ui/SectionHeader';
import { Badge } from '../../components/staff/ui/Badge';
import { EditProductSheet, type ProductEnriched } from '../../components/staff/EditProductSheet';
import { SortableProductList } from '../../components/staff/SortableProductList';
import { isCurrentUserAdmin } from '../../utils/auth';
import { apiFetch } from '../../utils/api';

interface ResolvedProduct {
  id: string;
  name: string;
  price: number;
  type: 'liquid' | 'solid' | 'other';
  available: boolean;
}
interface ResolvedNode {
  id: string;
  parentId: string | null;
  label: string;
  emoji: string | null;
  description: string | null;
  position: number;
  hidden: boolean;
  children: ResolvedNode[];
  products: ResolvedProduct[];
}
interface NavigationResponse {
  roots: ResolvedNode[];
}

/** Un produit + son statut de visibilité hérité (sous un nœud masqué ?). */
interface FlatProduct {
  product: ResolvedProduct;
  hidden: boolean;
}

/**
 * DFS : tous les produits sous un sous-arbre, dans l'ordre. Contrairement au
 * client (qui élague les nœuds masqués), le staff collecte TOUT et propage le
 * flag `hidden` hérité pour pouvoir le signaler visuellement.
 */
function collectProducts(node: ResolvedNode, inheritedHidden = false): FlatProduct[] {
  const nodeHidden = inheritedHidden || node.hidden;
  const out: FlatProduct[] = node.products.map((p) => ({ product: p, hidden: nodeHidden }));
  for (const child of node.children) {
    out.push(...collectProducts(child, nodeHidden));
  }
  return out;
}

export function StaffCarte() {
  const navigate = useNavigate();
  const [roots, setRoots] = useState<ResolvedNode[]>([]);
  const [enrichedMap, setEnrichedMap] = useState<Map<string, ProductEnriched>>(new Map());
  const [activeRootId, setActiveRootId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ResolvedProduct | null>(null);
  const [resyncing, setResyncing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const admin = useMemo(() => isCurrentUserAdmin(), []);

  // Réinjecte un enrichment fraîchement sauvegardé sans refetch (la map est la
  // source de vérité d'affichage, l'arbre ne porte pas l'enriched).
  const onEnrichmentSaved = (updated: ProductEnriched) => {
    setEnrichedMap((prev) => new Map(prev).set(updated.cashpadId, updated));
  };

  const loadData = useCallback(async () => {
    const token = localStorage.getItem('alterrego_jwt');

    // Admin → endpoint /admin (voit aussi les nœuds masqués + JWT). Fallback
    // public /pos/navigation si pas admin ou si l'/admin échoue (403/expiré) :
    // le public renvoie le même arbre (hidden inclus, non filtré côté serveur).
    const loadNavigation = async (): Promise<NavigationResponse> => {
      if (admin && token) {
        const r = await apiFetch(`/pos/navigation/admin`);
        if (r.ok) return r.json();
      }
      const pub = await apiFetch(`/pos/navigation`, { auth: false });
      return pub.ok ? pub.json() : { roots: [] };
    };

    try {
      const [nav, enriched] = await Promise.all([
        loadNavigation(),
        apiFetch(`/pos/products-enriched`, { auth: false }).then((r) => (r.ok ? r.json() : [])),
      ]) as [NavigationResponse, ProductEnriched[]];
      const map = new Map<string, ProductEnriched>();
      for (const e of enriched) map.set(e.cashpadId, e);
      const nextRoots = nav?.roots ?? [];
      setRoots(nextRoots);
      setEnrichedMap(map);
      setActiveRootId((cur) => cur ?? (nextRoots.length > 0 ? nextRoots[0].id : null));
      setError(null);
    } catch {
      setError('Données carte indisponibles');
    } finally {
      setLoading(false);
    }
  }, [admin]);

  useEffect(() => { void loadData(); }, [loadData]);

  // Resync carte : vide le cache backend (POST /resync) → re-fetch Cashpad
  // maintenant, puis recharge l'affichage. Utile quand Romaric vient de mettre
  // à jour sa carte sur Cashpad et veut la voir tout de suite (sinon cache 5 min).
  const handleResync = useCallback(async () => {
    const token = localStorage.getItem('alterrego_jwt');
    if (!token || resyncing) return;
    setResyncing(true);
    setToast(null);
    try {
      const r = await apiFetch(`/pos/navigation/resync`, {
        method: 'POST',
      });
      if (!r.ok) throw new Error('resync failed');
      const d = await r.json();
      await loadData();
      const unmappedCount = (d.unmapped ?? []).reduce((a: number, u: any) => a + (u.productCount ?? 0), 0);
      setToast(
        `Carte synchronisée — ${d.productsMapped} produits, ${d.sections} sections` +
          (unmappedCount > 0 ? ` · ${unmappedCount} non rangés` : ''),
      );
    } catch {
      setToast('Échec de la synchronisation. Réessayez.');
    } finally {
      setResyncing(false);
      setTimeout(() => setToast(null), 4500);
    }
  }, [loadData, resyncing]);

  // Réordonnancement drag-drop (par catégorie) : persiste le nouvel ordre.
  // L'optimistic est dans SortableProductList ; ici on persiste + resync `roots`
  // (le cache carte est busté côté serveur par la route reorder).
  const handleReorder = useCallback(async (orderedIds: string[]) => {
    const token = localStorage.getItem('alterrego_jwt');
    if (!token) return;
    try {
      const r = await apiFetch(`/pos/products-enriched/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: orderedIds }),
      });
      if (!r.ok) throw new Error('reorder failed');
      void loadData(); // sync (ordre identique à l'optimistic → pas de flash)
    } catch {
      setToast('Échec du réordonnancement, ordre rétabli.');
      void loadData();
      setTimeout(() => setToast(null), 3000);
    }
  }, [loadData]);

  const allProducts = useMemo(() => roots.flatMap((r) => collectProducts(r)), [roots]);
  const totalProducts = allProducts.length;
  const totalUnavailable = allProducts.filter((x) => !x.product.available).length;
  const totalHidden = allProducts.filter((x) => x.hidden).length;

  const activeRoot = useMemo(
    () => roots.find((r) => r.id === activeRootId) ?? roots[0] ?? null,
    [roots, activeRootId],
  );

  // Sections du root actif : une par enfant N2 (incl. masqués), ou le root
  // lui-même s'il n'a pas d'enfants.
  const sections = useMemo(() => {
    if (!activeRoot) return [];
    const children = activeRoot.children ?? [];
    if (children.length === 0) {
      return [{ node: activeRoot, items: collectProducts(activeRoot) }];
    }
    return children.map((child) => ({
      node: child,
      items: collectProducts(child, activeRoot.hidden),
    }));
  }, [activeRoot]);

  // Recherche : à plat sur tout l'arbre (set DFS complet), case-insensitive.
  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return allProducts.filter((x) => x.product.name.toLowerCase().includes(q));
  }, [allProducts, search]);

  const renderProductCard = ({ product: p, hidden }: FlatProduct) => {
    const clientHidden = enrichedMap.get(p.id)?.hiddenInClient === true;
    return (
    <div
      key={p.id}
      className={`staff-product-card ${!p.available ? 'unavailable' : ''} ${hidden || clientHidden ? 'hidden-node' : ''}`}
    >
      <div className="staff-product-info">
        <strong>{p.name}</strong>
      </div>
      <div className="staff-product-right">
        {clientHidden && <Badge tone="neutral" size="sm">Masqué client</Badge>}
        {hidden && <Badge tone="neutral" size="sm">Masqué</Badge>}
        {!p.available && <Badge tone="long" size="sm">Rupture</Badge>}
        <span className="staff-product-price">{p.price.toFixed(2)} €</span>
        {admin && (
          <button
            type="button"
            className="staff-product-edit"
            aria-label="Éditer la fiche"
            onClick={() => setEditing(p)}
          >
            ✏️
          </button>
        )}
      </div>
    </div>
    );
  };

  return (
    <div className="staff-page-v2 staff-page">
      <div className="staff-header-v2">
        <button className="staff-back-v2" onClick={() => navigate('/staff/dashboard')}>‹</button>
        <h1>La Carte</h1>
        <span className="staff-badge-v2">
          {totalProducts} prod
          {totalUnavailable > 0 ? ` · ${totalUnavailable} off` : ''}
          {totalHidden > 0 ? ` · ${totalHidden} masq.` : ''}
        </span>
      </div>

      {/* Search bar */}
      <div className="staff-search-row">
        <span className="staff-search-icon">🔍</span>
        <input
          type="text"
          className="staff-search-input"
          placeholder="Chercher un produit…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="staff-search-clear" onClick={() => setSearch('')}>×</button>
        )}
      </div>

      {admin && (
        <div className="staff-cat-nav-link">
          <button
            type="button"
            className="staff-cat-nav-btn"
            onClick={() => navigate('/staff/carte/navigation')}
          >
            🧭 Gérer la navigation
          </button>
          <button
            type="button"
            className="staff-cat-nav-btn"
            onClick={handleResync}
            disabled={resyncing}
          >
            {resyncing ? '⏳ Synchronisation…' : '🔄 Resync carte'}
          </button>
        </div>
      )}

      {toast && <div className="staff-toast-v2">{toast}</div>}

      {loading && <p className="staff-empty">Chargement…</p>}
      {error && <p className="staff-empty" style={{ color: 'var(--color-error)' }}>{error}</p>}

      {!loading && !error && roots.length === 0 && (
        <p className="staff-empty">
          La carte n'est pas encore organisée.
          {admin ? ' Configurez-la via « Gérer la navigation ».' : ''}
        </p>
      )}

      {/* Recherche : liste plate de tous les produits matchés */}
      {search && (
        <div className="staff-section-v2">
          {searchResults && searchResults.length > 0 ? (
            <div className="staff-product-list">
              {searchResults.map(renderProductCard)}
            </div>
          ) : (
            <p className="staff-empty">Aucun produit trouvé</p>
          )}
        </div>
      )}

      {/* Tabs niveau 1 (roots) — masqué en mode recherche */}
      {!search && roots.length > 0 && (
        <div className="staff-cat-tabs">
          {roots.map((root) => (
            <Pill
              key={root.id}
              active={root.id === activeRoot?.id}
              count={collectProducts(root).length}
              onClick={() => setActiveRootId(root.id)}
            >
              {root.emoji && <span style={{ marginRight: 4 }}>{root.emoji}</span>}
              {root.label}
              {root.hidden && <span style={{ marginLeft: 4, opacity: 0.6 }}>🙈</span>}
            </Pill>
          ))}
        </div>
      )}

      {/* Sections du root actif */}
      {!search && sections.map(({ node, items }) => (
        <div key={node.id} className="staff-section-v2">
          <SectionHeader
            title={`${node.emoji ? `${node.emoji} ` : ''}${node.label}`}
            caption={
              `${items.length} produit${items.length > 1 ? 's' : ''}` +
              (node.hidden ? ' · masqué côté client' : '')
            }
          />
          {items.length > 0 ? (
            <div className="staff-product-list">
              {admin ? (
                <SortableProductList
                  items={items}
                  isClientHidden={(id) => enrichedMap.get(id)?.hiddenInClient === true}
                  admin={admin}
                  onEdit={(p) => setEditing(p)}
                  onReorder={handleReorder}
                />
              ) : (
                items.map(renderProductCard)
              )}
            </div>
          ) : (
            <p className="staff-empty">Catégorie vide</p>
          )}
        </div>
      ))}

      {admin && (
        <EditProductSheet
          product={
            editing
              ? { id: editing.id, name: editing.name, enriched: enrichedMap.get(editing.id) ?? null }
              : null
          }
          onClose={() => setEditing(null)}
          onSaved={onEnrichmentSaved}
        />
      )}
    </div>
  );
}
